import type { ReadinessCheck, ReadinessStatus, ReadinessSummary } from '@agentwiki/shared'
import type { ChipTone } from '../hybrid'
import type { Upload } from '../../hooks/use-uploads'
import type { HomeWorkQueueItem } from './home-work-queue-table'

export interface HomeBriefing {
  heading: string
  body: string
}

interface ReadinessRisk {
  check: ReadinessCheck
  sectionLabel: string
}

function formatCount(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`
}

function statusPriority(status: ReadinessStatus) {
  if (status === 'blocked') return 0
  if (status === 'attention') return 1
  if (status === 'unknown') return 2
  return 3
}

export function toneForReadinessStatus(status: ReadinessStatus): ChipTone {
  if (status === 'ready') return 'ok'
  if (status === 'attention') return 'warn'
  if (status === 'blocked') return 'dgr'
  return 'mut'
}

export function labelForReadinessStatus(status: ReadinessStatus) {
  if (status === 'ready') return 'ready'
  if (status === 'attention') return 'review'
  if (status === 'blocked') return 'blocked'
  return 'unknown'
}

export function getReadinessRisks(summary?: ReadinessSummary): ReadinessRisk[] {
  return (summary?.sections ?? [])
    .flatMap((section) =>
      section.checks.map((check) => ({
        check,
        sectionLabel: section.label,
      })),
    )
    .filter(({ check }) => check.status !== 'ready')
    .sort((a, b) => statusPriority(a.check.status) - statusPriority(b.check.status))
}

export function findReadinessCheck(summary: ReadinessSummary | undefined, id: string) {
  return summary?.sections.flatMap((section) => section.checks).find((check) => check.id === id)
}

export function buildHomeBriefing({
  summary,
  isLoading,
  failedUploadCount,
  processingUploadCount,
  uploadStatusUnavailable = false,
}: {
  summary?: ReadinessSummary
  isLoading?: boolean
  failedUploadCount: number
  processingUploadCount: number
  uploadStatusUnavailable?: boolean
}): HomeBriefing {
  if (isLoading) {
    return {
      heading: 'Loading workspace readiness.',
      body: 'Home is checking tenant-backed readiness, uploads, credits, and agent access before naming the next action.',
    }
  }

  if (!summary) {
    return {
      heading: 'Readiness signals are unavailable.',
      body: 'Open storage, search, or agents to inspect the underlying workspace surfaces while readiness reloads.',
    }
  }

  const risks = getReadinessRisks(summary)
  const blockedCount = risks.filter(({ check }) => check.status === 'blocked').length
  const topRiskLabels = risks.slice(0, 3).map(({ check }) => check.label)
  const uploadSignals = [
    uploadStatusUnavailable ? 'upload status unavailable' : null,
    failedUploadCount > 0 ? formatCount(failedUploadCount, 'failed source') : null,
    processingUploadCount > 0 ? formatCount(processingUploadCount, 'indexing source') : null,
  ].filter(Boolean)

  let heading: string
  if (blockedCount > 0) {
    heading = `${formatCount(blockedCount, 'knowledge signal')} blocked.`
  } else if (failedUploadCount > 0) {
    heading =
      failedUploadCount === 1
        ? '1 source needs extraction review.'
        : `${failedUploadCount} sources need extraction review.`
  } else if (uploadStatusUnavailable) {
    heading = `${summary.score}% ready; upload status unavailable.`
  } else if (risks.length > 0) {
    heading = `${summary.score}% ready with ${formatCount(risks.length, 'signal')} to review.`
  } else if (processingUploadCount > 0) {
    heading =
      processingUploadCount === 1
        ? `${summary.score}% ready while 1 source indexes.`
        : `${summary.score}% ready while ${processingUploadCount} sources index.`
  } else {
    heading = 'All tracked knowledge signals are ready.'
  }

  const details = [
    topRiskLabels.length > 0
      ? `Top readiness risks: ${topRiskLabels.join(', ')}.`
      : 'No actionable readiness checks are open.',
    uploadSignals.length > 0
      ? `Upload queue has ${uploadSignals.join(' and ')}.`
      : 'Upload queue has no failed or indexing sources.',
  ]

  return {
    heading,
    body: details.join(' '),
  }
}

function statusForUpload(status: string | null | undefined) {
  if (status === 'failed') return { tone: 'dgr' as const, label: 'Failed OCR' }
  if (status === 'processing' || status === 'pending')
    return { tone: 'proc' as const, label: 'Indexing' }
  if (status === 'completed') return { tone: 'ok' as const, label: 'Ready' }
  return { tone: 'mut' as const, label: 'Needs review' }
}

function uploadQueueRow(file: Upload, owner: string): HomeWorkQueueItem {
  const status = statusForUpload(file.extractionStatus)
  return {
    id: file.id,
    item: file.filename,
    source: file.fileKey,
    owner,
    age: 'current',
    tone: status.tone,
    signal: status.label,
    href: '/storage',
  }
}

export function buildHomeWorkQueue({
  activeUploads,
  readiness,
  owner,
  uploadStatusUnavailable = false,
  storageEnabled = true,
  limit = 3,
}: {
  activeUploads: Upload[]
  readiness?: ReadinessSummary
  owner: string
  uploadStatusUnavailable?: boolean
  storageEnabled?: boolean
  limit?: number
}): HomeWorkQueueItem[] {
  const uploadRows = !storageEnabled
    ? []
    : uploadStatusUnavailable
      ? [
          {
            id: 'uploads-unavailable',
            item: 'Storage upload status',
            source: '/storage',
            owner: 'system',
            age: 'current',
            tone: 'warn' as const,
            signal: 'Unavailable',
            href: '/storage',
          },
        ]
      : activeUploads.map((file) => uploadQueueRow(file, owner))
  const readinessRows = getReadinessRisks(readiness).map(({ check, sectionLabel }) => ({
    id: `readiness-${check.id}`,
    item: check.label,
    source: `readiness/${sectionLabel}`,
    owner: 'readiness',
    age: 'current',
    tone: toneForReadinessStatus(check.status),
    signal: labelForReadinessStatus(check.status),
    href:
      check.route && (storageEnabled || !check.route.startsWith('/storage'))
        ? check.route
        : undefined,
  }))

  return [...uploadRows, ...readinessRows].slice(0, limit)
}
